import React from "react";
import { formatSingleAmount } from "../utils/currency";
import { FanName, FanRowAvatar } from "./FansLeaderboardSheet";
import type { VisitCounts } from "../types/orders";
import type { DjFan, FansMeta, VisitRange } from "../hooks/useFans";
import { fanLastSeen } from "../utils/requestsFormat";
import { t, formatLocale } from "../i18n";

// The Fans sheet on the Requests page: the Visitors card with its range
// chips, then the all-time fan rows. Split out of pages/RequestsPage.tsx;
// the data comes from hooks/useFans.

const VISIT_RANGES: Array<{ key: VisitRange; chip: string; sub: string }> = [
  { key: "d1", chip: t("fansModal.chip1d"), sub: t("fansModal.last24Hours") },
  { key: "d7", chip: t("fansModal.chip7d"), sub: t("fansModal.last7Days") },
  { key: "d30", chip: t("fansModal.chip30d"), sub: t("fansModal.last30Days") },
  { key: "all", chip: t("fansModal.chipAll"), sub: t("fansModal.allTime") },
];

type Props = {
  visits: VisitCounts | null;
  visitRange: VisitRange;
  setVisitRange: (range: VisitRange) => void;
  fans: DjFan[];
  fansMeta: FansMeta | null;
  onClose: () => void;
};

export default function FansModal({ visits, visitRange, setVisitRange, fans, fansMeta, onClose }: Props) {
  return (
    <div
      className="soon-modal-overlay"
      onClick={onClose}
    >
      <div className="soon-modal fans-modal" onClick={(e) => e.stopPropagation()}>
        <h3>{t("fansModal.fans")}</h3>
        {visits && (
          <div className="fans-visits-card">
            <div className="fans-visits-top">
              <span className="fans-visits-eye" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2 12s3.5-6.5 10-6.5S22 12 22 12s-3.5 6.5-10 6.5S2 12 2 12z" />
                  <circle cx="12" cy="12" r="2.6" />
                </svg>
              </span>
              <span className="fans-visits-lbl">{t("fansModal.visitors")}</span>
            </div>
            <div className="fans-visits-num">{visits[visitRange].toLocaleString()}</div>
            <div className="fans-visits-sub">
              {visitRange === "all" && visits.since
                ? t("fansModal.peopleOpenedSince", { date: new Date(visits.since).toLocaleDateString(formatLocale(), { month: "short", day: "numeric" }) })
                : t("fansModal.peopleOpenedRange", { range: VISIT_RANGES.find((r) => r.key === visitRange)!.sub })}
            </div>
            <div className="fans-visits-chips" role="tablist" aria-label={t("fansModal.visitorRange")}>
              {VISIT_RANGES.map((r) => (
                <button
                  key={r.key}
                  type="button"
                  role="tab"
                  aria-selected={visitRange === r.key}
                  className={`fans-visits-chip${visitRange === r.key ? " on" : ""}`}
                  onClick={() => setVisitRange(r.key)}
                >
                  {r.chip}
                </button>
              ))}
            </div>
          </div>
        )}
        {fans.length === 0 ? (
          <p style={{ color: "#9ca3af" }}>{t("fansModal.noFansYetThey")}</p>
        ) : (
          <>
            <div className="fans-head">
              <span>{t("fansModal.yourFansAllTime")}</span>
              {fansMeta && (
                <span className="fans-head-r">
                  {t("fansModal.fansCount", { count: fansMeta.totalFans })} · {t("fansModal.requestsCount", { count: fansMeta.totalRequests })}
                </span>
              )}
            </div>
            <ol className="flb-list fans-rows">
              {fans.map((f, i) => (
                <li className="flb-row" key={`${f.name}-${i}`}>
                  <FanRowAvatar fan={f} />
                  <span className="flb-meta">
                    <FanName fan={f} />
                    <span className="flb-sub">
                      {t("fansModal.requestsCount", { count: f.requests })}
                      {f.lastSeen ? ` · ${fanLastSeen(f.lastSeen)}` : ""}
                    </span>
                  </span>
                  <span className="flb-spent">
                    {f.spent > 0 && f.currency ? formatSingleAmount(f.spent, f.currency) : "—"}
                  </span>
                </li>
              ))}
            </ol>
            {fansMeta && fansMeta.anonymousRequests > 0 && (
              <div className="fans-anon">
                {t("fansModal.plusAnonymousRequests", { count: fansMeta.anonymousRequests })}
              </div>
            )}
          </>
        )}
        <button onClick={onClose}>{t("fansModal.close")}</button>
      </div>
    </div>
  );
}
